// Sync: catching up with every company.
//
// Called at start-up, on becoming visible, and periodically in the foreground.
// Each company is polled separately and a failure with one does not interrupt
// the others — a direct consequence of there being no shared point whose outage
// takes everything down.
//
// A sync pass deliberately does NOT re-check every contact against the directory:
// with resolution now a gated live query per number, that would turn every app
// open into one ask per contact. The directory check moved to where it matters —
// entering a conversation (the held loop below), throttled by the repository's
// entry freshness window. A contact nobody opens keeps its last known status,
// which risks nothing: a thread nobody touches sends nothing.
//
// Web push wakes the installed app: a wake may carry a preview encrypted end to
// end (shown by the service worker without any fetching), and the events
// themselves always arrive through these fetches. While the app is open, the
// held request in an open conversation and the foreground timer are the
// delivery paths; without a push subscription they are the only ones — the
// spec'd fallback mode.

import { config } from '$lib/config';
import { deleteDeviceKey } from '$lib/crypto/device-keys';
import { renewCapabilities } from '$lib/push/push';
import {
  allContacts,
  contactById,
  migrateToSuccessor,
  saveCursor,
  saveFromEntry,
} from '$lib/db/contacts';
import { db, type ContactRow } from '$lib/db/db';
import { saveEvents } from '$lib/db/threads';
import { refreshViewList } from '$lib/db/views';
import { lookupByKey, lookupNumber, refreshDirectory } from '$lib/directory/directory-repo';
import { threadState } from '$lib/state/thread-state.svelte';

import { exportHistory, getEvents } from './distributor-api';
import { asClientError, type ErrorCode } from './errors';
import { retryDelayMs } from './http';
import { processOutbox } from './outbox';
import { sessionToken } from './session';
import { parseEvent, type ThreadState } from './types';

export interface SyncResult {
  newEvents: number;
  errors: Record<string, ErrorCode>;
}

let running = false;

/** One fetch per contact at a time. Without it a held request and an ordinary
 *  pass over the same contact could both be reading events and would write the
 *  cursor one after the other, the later write winning with the older value.
 *  A second caller is skipped rather than queued. */
const fetching = new Set<string>();

/** A full pass: refresh the directory manifest, drain the outbox, fetch events. */
export async function syncAll(): Promise<SyncResult> {
  if (running) {
    return { newEvents: 0, errors: {} };
  }
  running = true;
  try {
    const errors: Record<string, ErrorCode> = {};

    try {
      await refreshDirectory();
    } catch (error) {
      errors['directory'] = asClientError(error).code;
    }

    // The outbox drains first: anything queued goes out, and the replies it
    // provokes are picked up by the very same pass instead of the next one.
    await processOutbox();

    // Companies are independent servers, so they are asked at the same time:
    // opening the app should not take as long as the slowest one plus every
    // other one in front of it.
    const contacts = await allContacts();
    const counts = await Promise.all(
      contacts.map(async (contact) => {
        try {
          const count = await fetchEvents(contact);
          // Renewal rides the same pass (as in the native client): the fetch
          // just proved the session, and a push subscription that exists gets
          // its capability, notification key and wake mutes refreshed. A no-op
          // without a subscription.
          await renewCapabilities([contact.accountId]);
          return count;
        } catch (error) {
          errors[contact.accountId] = asClientError(error).code;
          return 0;
        }
      }),
    );
    return { newEvents: counts.reduce((a, b) => a + b, 0), errors };
  } finally {
    running = false;
  }
}

/** A single contact — after sending, to pick up the reply, and as the body of the
 *  held loop. The outbox drains first for the same reason as in syncAll. */
export async function syncContact(
  accountId: string,
  options?: { waitS?: number; signal?: AbortSignal },
): Promise<number> {
  const contact = await contactById(accountId);
  if (contact === undefined) {
    return 0;
  }
  await processOutbox();
  return fetchEvents(contact, options);
}

/** Re-checks one contact against the directory: a withdrawal, a move, a server
 *  change. Called on entering that contact's conversation — the repository's
 *  freshness window (30 min) is the throttle, so opening the same thread twice in
 *  a row costs one network ask, not two. Returns the contact to keep syncing
 *  with, which after a signed move is the successor. */
export async function refreshContactFromDirectory(
  accountId: string,
): Promise<ContactRow | undefined> {
  const contact = await contactById(accountId);
  if (contact === undefined) {
    return undefined;
  }
  return updateFromDirectory(contact);
}

async function updateFromDirectory(contact: ContactRow): Promise<ContactRow | undefined> {
  const entry = await lookupNumber(contact.phone).catch(() => undefined);
  if (entry === undefined || entry.accountId !== contact.accountId) {
    // The number resolves to nothing, or to a different account without a move
    // having been announced on this one. Neither is a reason to touch a working
    // contact.
    return contact;
  }

  // A signed move proves continuity, so the client follows the successor on its
  // own. An unsigned one waits out the pending period — the `moved` status alone
  // already holds sending, which is the safe state while ownership is unclear.
  if (entry.status === 'moved' && entry.successorSigned && entry.successorKey !== undefined) {
    const successor = await lookupByKey(entry.successorKey);
    if (
      successor !== undefined &&
      successor.status === 'active' &&
      successor.accountId !== contact.accountId
    ) {
      await migrateToSuccessor(contact.accountId, successor);
      await deleteDeviceKey(contact.accountId);
      return contactById(successor.accountId);
    }
  }

  await saveFromEntry(entry);

  // A withdrawal turns the thread read-only. Before the server stops answering it
  // is worth taking the history — after that, only what is local remains.
  if (entry.status === 'withdrawn') {
    await saveHistoryLocally(contact);
  }
  return contactById(contact.accountId);
}

async function fetchEvents(
  contact: ContactRow,
  options?: { waitS?: number; signal?: AbortSignal },
): Promise<number> {
  if (contact.token === null || contact.threadId === null) {
    return 0;
  }
  if (fetching.has(contact.accountId)) {
    return 0;
  }
  fetching.add(contact.accountId);
  try {
    return await fetchEventsAlone(contact, options);
  } finally {
    fetching.delete(contact.accountId);
  }
}

async function fetchEventsAlone(
  contact: ContactRow,
  options?: { waitS?: number; signal?: AbortSignal },
): Promise<number> {
  let total = 0;
  let cursor = contact.cursor ?? undefined;
  let more = true;

  // Only the first request of a pass may be held: once the server has said there
  // is something, the rest of the pages are already waiting and asking it to wait
  // again would stall on a page that exists.
  let holdFor = options?.waitS;

  while (more) {
    const fresh = (await contactById(contact.accountId)) ?? contact;
    const token = await sessionToken(fresh);
    const page = await getEvents({
      serverUrl: fresh.serverUrl,
      accountId: fresh.accountId,
      token,
      ...(cursor !== undefined ? { cursor } : {}),
      ...(holdFor !== undefined ? { waitS: holdFor } : {}),
      ...(options?.signal !== undefined ? { signal: options.signal } : {}),
    });
    holdFor = undefined;

    await saveEvents(fresh.accountId, page.events);
    total += page.events.length;

    // What the other side is doing, and who that other side is. Kept out of the
    // database on purpose: it is true now and false a moment later, and a status
    // restored from disk after a restart would be a lie about the present.
    threadState.apply(fresh.accountId, page.threadState);

    // Company pages, if any moved. The counter is the only cue there is — a page is not
    // an event, so nothing else would ever tell us. Failures are swallowed: a stale menu
    // is a smaller problem than an event sync that stops because a catalogue was
    // unreachable.
    await syncViewsIfMoved(fresh.accountId, page.threadState);

    const highest = page.events.reduce((a, b) => Math.max(a, b.sequence), fresh.lastSequence);
    await saveCursor(fresh.accountId, page.nextCursor, highest);

    cursor = page.nextCursor;
    more = page.hasMore && page.events.length > 0;
  }
  return total;
}

async function saveHistoryLocally(contact: ContactRow): Promise<void> {
  if (contact.token === null) {
    return;
  }
  try {
    const token = await sessionToken(contact);
    const bundle = await exportHistory({
      serverUrl: contact.serverUrl,
      accountId: contact.accountId,
      token,
    });
    const rawEvents = Array.isArray(bundle['events']) ? bundle['events'] : [];
    const events = rawEvents
      .filter((e): e is Record<string, unknown> => typeof e === 'object' && e !== null)
      .map(parseEvent);
    await saveEvents(contact.accountId, events);
  } catch {
    // A withdrawn company's server may already be gone. What remains is what is
    // local — precisely the outcome this save is meant to prevent, when it gets
    // there in time.
  }
}

// ——————————————————————————————————————————————————————————————
// Held loop and foreground polling
// ——————————————————————————————————————————————————————————————

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms);
    function done(): void {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    }
    signal.addEventListener('abort', done);
  });
}

/**
 * The open conversation's loop: one plain pass first (asking "what is true
 * now"), then held requests back to back, so a question is outstanding at all
 * times and a message arrives as fast as the network allows. A 500 ms floor
 * guards against a chatty server answering holds instantly; errors back off
 * exponentially with jitter. Aborting [signal] ends the loop and the request in
 * flight.
 */
export async function runHeldLoop(accountId: string, signal: AbortSignal): Promise<void> {
  let attempt = 0;
  // Entering the conversation is the moment the directory check happens — the one
  // place where an unnoticed withdrawal or move would matter. Throttled to one ask
  // per half hour per contact by the repository; a failure changes nothing here.
  let currentId = accountId;
  try {
    const fresh = await refreshContactFromDirectory(accountId);
    if (fresh !== undefined) {
      currentId = fresh.accountId;
    }
  } catch {
    // A directory that cannot be asked leaves the contact as it was.
  }
  try {
    await syncContact(currentId);
  } catch {
    // The held loop below will retry with backoff.
  }
  while (!signal.aborted) {
    const started = Date.now();
    try {
      await syncContact(currentId, { waitS: config.eventHoldS, signal });
      attempt = 0;
      if (signal.aborted) {
        return;
      }
      if (Date.now() - started < 1000) {
        await sleep(500, signal);
      }
    } catch {
      if (signal.aborted) {
        return;
      }
      attempt++;
      await sleep(retryDelayMs(attempt), signal);
    }
  }
}

let pollTimer: ReturnType<typeof setInterval> | null = null;

export function startForegroundPolling(): void {
  stopForegroundPolling();
  pollTimer = setInterval(() => {
    void syncAll();
  }, config.pollingIntervalMs);
}

export function stopForegroundPolling(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/** Web equivalent of the native resumed/paused rules: hidden ⇒ no timers and no
 *  held requests; visible ⇒ a full pass and the timer back. The browser's own
 *  `online` event cuts through any backoff in progress — connectivity returning
 *  is a fact, not something to rediscover by waiting out an exponential delay. */
export function attachLifecycle(): void {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopForegroundPolling();
    } else {
      void syncAll();
      startForegroundPolling();
    }
  });
  window.addEventListener('online', () => {
    void (async () => {
      const { outboxOnline } = await import('./outbox');
      await outboxOnline();
      await syncAll();
    })();
  });
}

/** Refetches the page list when the thread's `views_version` differs from the one behind
 *  what is cached. Persisting the number is what keeps an ordinary app open from costing
 *  one request per contact for a menu nobody changed. */
async function syncViewsIfMoved(accountId: string, states: ThreadState[]): Promise<void> {
  const state = states[0];
  if (state === undefined) {
    return;
  }
  const contact = await db.contacts.get(accountId);
  if (contact === undefined || contact.viewsVersion === state.viewsVersion) {
    return;
  }
  try {
    await refreshViewList(accountId);
    await db.contacts.update(accountId, { viewsVersion: state.viewsVersion });
  } catch {
    // Left for the next pass. The counter stays unchanged, so the next sync retries.
  }
}
